import React, { Component } from "react";
import PropTypes from "prop-types";
import { area as d3Area } from "d3-shape";

import GenericChartComponent from "../../GenericChartComponent";
import { getAxisCanvas } from "../../GenericComponent";
import {
  hexToRGBA,
  isDefined,
  strokeDashTypes,
  getStrokeDasharray,
} from "../../utils";

class BaselineAreaSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);

    // Cache gradients to prevent recreation
    this.gradientCache = new Map();
  }

  drawOnCanvas(ctx, moreProps) {
    const { yAccessor, defined, separatorPrice, isAbove } = this.props;
    const { fill, opacity, interpolation, canvasClip, stroke } = this.props;

    const strokeColor = stroke?.color || "none";
    const strokeOpacity = stroke?.opacity ?? 1;
    const strokeWidth = stroke?.width ?? 1;
    const strokeDasharray = stroke?.dash ?? "Solid";

    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    if (separatorPrice == null) return;

    // Save canvas state
    ctx.save();

    if (canvasClip) {
      canvasClip(ctx, moreProps);
    }

    // Use simple solid fill instead of gradient to eliminate flickering.
    // Simple solid fill with reduced opacity for smooth appearance;
    // invalid fill resolves to "transparent" (stroke still draws).
    ctx.fillStyle = hexToRGBA(fill, opacity * 0.3);

    // Set composite operation to prevent overlap issues
    ctx.globalCompositeOperation = "source-over";

    ctx.strokeStyle = hexToRGBA(strokeColor, strokeOpacity);
    ctx.lineWidth = strokeWidth;
    ctx.setLineDash(getStrokeDasharray(strokeDasharray).split(","));

    ctx.beginPath();

    const separatorY = yScale(separatorPrice);
    const areaSeries = d3Area()
      .defined((d) => defined(yAccessor(d)))
      .x((d) => Math.round(xScale(xAccessor(d))))
      .y0(() => separatorY) // Always use separator as baseline
      .y1((d) => {
        const value = yAccessor(d);
        if (isAbove) {
          // For above area, show only when above separator, otherwise clamp to separator
          return value >= separatorPrice
            ? Math.round(yScale(value))
            : separatorY;
        } else {
          // For below area, show only when below separator, otherwise clamp to separator
          return value <= separatorPrice
            ? Math.round(yScale(value))
            : separatorY;
        }
      })
      .context(ctx);

    if (isDefined(interpolation)) {
      areaSeries.curve(interpolation);
    }

    areaSeries(plotData);
    ctx.fill();

    // Optionally stroke the area boundary
    if (strokeColor !== "none" && strokeWidth > 0) {
      ctx.stroke();
    }

    // Restore canvas state
    ctx.restore();
  }

  renderSVG(moreProps) {
    const {
      yAccessor,
      defined,
      separatorPrice,
      isAbove,
      style,
      stroke,
      fill,
      className,
      opacity,
      interpolation,
    } = this.props;

    const strokeColor = stroke?.color || "none";
    const strokeOpacity = stroke?.opacity ?? 1;
    const strokeWidth = stroke?.width ?? 1;
    const strokeDasharray = stroke?.dash ?? "Solid";

    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    if (separatorPrice == null) return null;

    const separatorY = yScale(separatorPrice);

    const areaSeries = d3Area()
      .defined((d) => {
        const value = yAccessor(d);
        const isValidValue = defined(value);

        if (isAbove) {
          return isValidValue && value >= separatorPrice;
        } else {
          return isValidValue && value <= separatorPrice;
        }
      })
      .x((d) => Math.round(xScale(xAccessor(d))))
      .y0(() => separatorY)
      .y1((d) => {
        const value = yAccessor(d);
        if (isAbove) {
          return Math.round(yScale(Math.max(value, separatorPrice)));
        } else {
          return Math.round(yScale(Math.min(value, separatorPrice)));
        }
      });

    if (isDefined(interpolation)) {
      areaSeries.curve(interpolation);
    }

    const d = areaSeries(plotData);
    const newClassName = className.concat(
      isDefined(strokeColor) ? "" : " line-stroke",
    );

    return (
      <path
        style={style}
        d={d}
        stroke={strokeColor}
        strokeOpacity={strokeOpacity}
        strokeWidth={strokeWidth}
        strokeDasharray={getStrokeDasharray(strokeDasharray)}
        fill={hexToRGBA(fill, opacity)}
        className={newClassName}
      />
    );
  }

  render() {
    const { separatorPrice, isAbove } = this.props;

    // Only render if separator exists and we have a reason to render this area
    if (separatorPrice == null) return null;

    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        canvasToDraw={getAxisCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={[]}
        key={`baseline-area-${isAbove ? "above" : "below"}-${separatorPrice}`}
      />
    );
  }
}

BaselineAreaSeries.propTypes = {
  stroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.oneOf(strokeDashTypes),
  }),
  fill: PropTypes.string.isRequired,
  opacity: PropTypes.number.isRequired,
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  separatorPrice: PropTypes.number,
  isAbove: PropTypes.bool.isRequired,
  defined: PropTypes.func,
  interpolation: PropTypes.func,
  canvasClip: PropTypes.func,
  canvasGradient: PropTypes.func,
  style: PropTypes.object,
};

BaselineAreaSeries.defaultProps = {
  stroke: {
    color: "none",
    opacity: 1,
    width: 0,
    dash: "Solid",
  },
  opacity: 0.2,
  className: "react-stockcharts-baseline-area",
  defined: (d) => d != null,
};

export default BaselineAreaSeries;
